/**
 * KYBReviewStep Component
 * =======================
 * Final review step for KYB verification showing all collected data.
 * Allows editing each section before final submission.
 */

import React, { useState, useEffect } from 'react';
import {
  ClipboardCheck,
  ChevronLeft,
  AlertCircle,
  Building2,
  Users,
  FileText,
  Edit3,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  MapPin,
  Globe,
  Calendar,
  User,
  Percent,
  Mail,
  File,
} from 'lucide-react';

const SectionHeader = ({ icon: Icon, title, onEdit, isComplete }) => (
  <div className="section-header">
    <div className="section-header-left">
      <div className={`section-icon ${isComplete ? 'complete' : ''}`}>
        <Icon size={18} />
      </div>
      <h3 className="section-title">{title}</h3>
      {isComplete && (
        <div className="complete-indicator">
          <CheckCircle2 size={14} />
        </div>
      )}
    </div>
    {onEdit && (
      <button type="button" className="edit-btn" onClick={onEdit}>
        <Edit3 size={14} />
        Edit
      </button>
    )}
  </div>
);

const DataRow = ({ label, value, icon: Icon }) => (
  <div className="data-row">
    {Icon && <Icon size={14} className="data-icon" />}
    <span className="data-label">{label}</span>
    <span className="data-value">{value || '-'}</span>
  </div>
);

const formatEntityType = (type) => {
  const types = {
    llc: 'Limited Liability Company (LLC)',
    corporation: 'Corporation (C-Corp)',
    s_corporation: 'S-Corporation',
    partnership: 'Partnership',
    sole_proprietorship: 'Sole Proprietorship',
    nonprofit: 'Non-Profit Organization',
    cooperative: 'Cooperative',
    trust: 'Trust',
  };
  return types[type] || type;
};

const formatIndustry = (industry) => {
  const industries = {
    technology: 'Technology',
    finance: 'Financial Services',
    healthcare: 'Healthcare',
    retail: 'Retail & E-Commerce',
    manufacturing: 'Manufacturing',
    real_estate: 'Real Estate',
    professional_services: 'Professional Services',
    hospitality: 'Hospitality & Tourism',
    education: 'Education',
    construction: 'Construction',
    agriculture: 'Agriculture',
    transportation: 'Transportation & Logistics',
    media: 'Media & Entertainment',
    energy: 'Energy & Utilities',
    crypto: 'Cryptocurrency & Blockchain',
  };
  return industries[industry] || industry;
};

const formatCountry = (code) => {
  const countries = {
    US: 'United States',
    CA: 'Canada',
    GB: 'United Kingdom',
    DE: 'Germany',
    FR: 'France',
    AU: 'Australia',
    SG: 'Singapore',
    HK: 'Hong Kong',
    JP: 'Japan',
    CH: 'Switzerland',
    NL: 'Netherlands',
    IE: 'Ireland',
    IN: 'India',
    CN: 'China',
    BR: 'Brazil',
    MX: 'Mexico',
  };
  return countries[code] || code;
};

const formatAddress = (address) => {
  if (!address) return '-';
  const parts = [
    address.line_1,
    address.line_2,
    address.city,
    address.state,
    address.postal_code,
    formatCountry(address.country),
  ].filter(Boolean);
  return parts.join(', ');
};

export default function KYBReviewStep({
  config = {},
  branding = {},
  sessionData = {},
  onComplete,
  onBack,
  onError,
  onEditSection,
  apiClient,
}) {
  const [animateIn, setAnimateIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Consent checkboxes
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [certifiedAccurate, setCertifiedAccurate] = useState(false);

  const { company_info, beneficial_owners, corporate_documents } = sessionData || {};

  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Calculate completeness
  const isCompanyInfoComplete = Boolean(company_info?.legal_name);
  const isUBOComplete = beneficial_owners?.length > 0;
  const isDocumentsComplete = corporate_documents && Object.keys(corporate_documents).length > 0;

  const handleSubmit = async () => {
    const newErrors = {};

    if (!acceptedTerms) {
      newErrors.terms = 'You must accept the terms and conditions';
    }
    if (!certifiedAccurate) {
      newErrors.certification = 'You must certify the information is accurate';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      // Submit final KYB verification
      if (apiClient?.submit) {
        await apiClient.submit();
      }

      onComplete({
        submitted: true,
        submission_timestamp: new Date().toISOString(),
      });
    } catch (err) {
      console.error('Failed to submit KYB verification:', err);
      if (onError) {
        onError(err);
      }
      setErrors({ submit: err.message || 'Failed to submit verification' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSection = (sectionName) => {
    if (onEditSection) {
      onEditSection(sectionName);
    }
  };

  return (
    <div className={`sdk-step kyb-review-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header">
        {onBack && (
          <button className="back-button" onClick={onBack} type="button">
            <ChevronLeft size={20} />
          </button>
        )}
        <div className="step-icon">
          <ClipboardCheck size={24} />
        </div>
      </div>

      <h2 className="step-title">Review & Submit</h2>
      <p className="step-description">
        Please review your information before submitting for verification.
      </p>

      <div className="review-sections">
        {/* Company Information */}
        <div className="review-section">
          <SectionHeader
            icon={Building2}
            title="Company Information"
            isComplete={isCompanyInfoComplete}
            onEdit={() => handleEditSection('company_info')}
          />
          {company_info ? (
            <div className="section-content">
              <DataRow label="Legal Name" value={company_info.legal_name} />
              {company_info.trading_name && (
                <DataRow label="Trading Name" value={company_info.trading_name} />
              )}
              <DataRow
                label="Entity Type"
                value={formatEntityType(company_info.entity_type)}
              />
              <DataRow
                label="Industry"
                value={formatIndustry(company_info.industry)}
              />
              <DataRow
                label="Country of Incorporation"
                value={formatCountry(company_info.country_of_incorporation)}
                icon={Globe}
              />
              {company_info.registration_number && (
                <DataRow label="Registration Number" value={company_info.registration_number} />
              )}
              {company_info.tax_id && (
                <DataRow label="Tax ID" value={company_info.tax_id} />
              )}
              {company_info.website && (
                <DataRow label="Website" value={company_info.website} icon={Globe} />
              )}
              <DataRow
                label="Business Address"
                value={formatAddress(company_info.address)}
                icon={MapPin}
              />
            </div>
          ) : (
            <div className="section-empty">
              <AlertCircle size={16} />
              No company information provided
            </div>
          )}
        </div>

        {/* Beneficial Owners */}
        <div className="review-section">
          <SectionHeader
            icon={Users}
            title="Beneficial Owners"
            isComplete={isUBOComplete}
            onEdit={() => handleEditSection('beneficial_owners')}
          />
          {beneficial_owners?.length > 0 ? (
            <div className="section-content">
              {beneficial_owners.map((ubo, index) => (
                <div key={index} className="ubo-summary">
                  <div className="ubo-header">
                    <div className="ubo-avatar">
                      <User size={14} />
                    </div>
                    <div className="ubo-info">
                      <span className="ubo-name">{ubo.full_name}</span>
                      <span className="ubo-ownership">{ubo.ownership_percentage}% ownership</span>
                    </div>
                  </div>
                  <div className="ubo-details">
                    <DataRow label="Nationality" value={formatCountry(ubo.nationality)} />
                    <DataRow label="Date of Birth" value={ubo.date_of_birth} icon={Calendar} />
                    {ubo.email && <DataRow label="Email" value={ubo.email} icon={Mail} />}
                    {ubo.is_control_person && (
                      <div className="ubo-badge">Control Person</div>
                    )}
                    {ubo.is_submitter && (
                      <div className="ubo-badge submitter">Submitter</div>
                    )}
                  </div>
                </div>
              ))}
              <div className="total-ownership">
                <Percent size={14} />
                <span>Total Ownership: {beneficial_owners.reduce((sum, ubo) => sum + (ubo.ownership_percentage || 0), 0)}%</span>
              </div>
            </div>
          ) : (
            <div className="section-empty">
              <AlertCircle size={16} />
              No beneficial owners provided
            </div>
          )}
        </div>

        {/* Corporate Documents */}
        <div className="review-section">
          <SectionHeader
            icon={FileText}
            title="Corporate Documents"
            isComplete={isDocumentsComplete}
            onEdit={() => handleEditSection('corporate_documents')}
          />
          {corporate_documents && Object.keys(corporate_documents).length > 0 ? (
            <div className="section-content">
              <div className="documents-list">
                {Object.entries(corporate_documents).map(([docType, doc]) => (
                  <div key={docType} className="document-item">
                    <div className="document-check">
                      <CheckCircle2 size={14} />
                    </div>
                    <div className="document-info">
                      <span className="document-type">{formatDocumentType(docType)}</span>
                      <span className="document-file">
                        <File size={12} />
                        {doc.file_name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="section-empty">
              <AlertCircle size={16} />
              No documents uploaded
            </div>
          )}
        </div>
      </div>

      {/* Consent Section */}
      <div className="consent-section">
        <label className={`consent-checkbox ${errors.terms ? 'error' : ''}`}>
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => {
              setAcceptedTerms(e.target.checked);
              if (errors.terms) setErrors(prev => ({ ...prev, terms: null }));
            }}
          />
          <div className="checkbox-custom">
            {acceptedTerms && <CheckCircle2 size={12} />}
          </div>
          <span className="consent-text">
            I agree to the <a href="#terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{' '}
            <a href="#privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          </span>
        </label>
        {errors.terms && (
          <span className="consent-error">
            <AlertCircle size={12} />
            {errors.terms}
          </span>
        )}

        <label className={`consent-checkbox certification ${errors.certification ? 'error' : ''}`}>
          <input
            type="checkbox"
            checked={certifiedAccurate}
            onChange={(e) => {
              setCertifiedAccurate(e.target.checked);
              if (errors.certification) setErrors(prev => ({ ...prev, certification: null }));
            }}
          />
          <div className="checkbox-custom">
            {certifiedAccurate && <ShieldCheck size={12} />}
          </div>
          <span className="consent-text">
            <strong>I certify</strong> that all information provided is accurate and complete,
            and I am authorized to submit this verification on behalf of the company.
          </span>
        </label>
        {errors.certification && (
          <span className="consent-error">
            <AlertCircle size={12} />
            {errors.certification}
          </span>
        )}
      </div>

      {errors.submit && (
        <div className="submit-error">
          <AlertCircle size={16} />
          {errors.submit}
        </div>
      )}

      <div className="step-actions">
        <button
          type="button"
          className="primary-button"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="spin" />
              Submitting...
            </>
          ) : (
            <>
              <ShieldCheck size={18} />
              Submit Verification
            </>
          )}
        </button>
      </div>

      <p className="security-notice">
        <ShieldCheck size={12} />
        Your information is encrypted and securely processed
      </p>

      <style>{kybReviewStyles}</style>
    </div>
  );
}

// Helper function to format document type names
function formatDocumentType(type) {
  const types = {
    certificate_of_incorporation: 'Certificate of Incorporation',
    articles_of_association: 'Articles of Association',
    proof_of_address: 'Proof of Business Address',
    ownership_structure: 'Ownership Structure Document',
    good_standing: 'Certificate of Good Standing',
    tax_registration: 'Tax Registration Certificate',
  };
  return types[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const kybReviewStyles = `
  .kyb-review-step {
    padding: 24px;
    max-width: 560px;
    margin: 0 auto;
  }

  .step-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
  }

  .back-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: var(--sdk-text-secondary, #6B7280);
    border-radius: 8px;
    transition: all 0.2s;
  }

  .back-button:hover {
    background: var(--sdk-bg-secondary, #F3F4F6);
    color: var(--sdk-text, #1F2937);
  }

  .step-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: var(--sdk-primary, #0D9488);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .step-title {
    font-size: 24px;
    font-weight: 600;
    color: var(--sdk-text, #1F2937);
    margin: 0 0 8px;
  }

  .step-description {
    color: var(--sdk-text-secondary, #6B7280);
    font-size: 15px;
    margin: 0 0 24px;
    line-height: 1.5;
  }

  /* Review Sections */
  .review-sections {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
  }

  .review-section {
    background: white;
    border: 2px solid var(--sdk-border, #E5E7EB);
    border-radius: 14px;
    overflow: hidden;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: var(--sdk-bg-secondary, #F9FAFB);
    border-bottom: 1px solid var(--sdk-border, #E5E7EB);
  }

  .section-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .section-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: white;
    border: 1px solid var(--sdk-border, #E5E7EB);
    color: var(--sdk-text-secondary, #6B7280);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
  }

  .section-icon.complete {
    background: var(--sdk-primary, #0D9488);
    border-color: var(--sdk-primary, #0D9488);
    color: white;
  }

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--sdk-text, #1F2937);
    margin: 0;
  }

  .complete-indicator {
    color: #059669;
    display: flex;
    align-items: center;
  }

  .edit-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: white;
    border: 1px solid var(--sdk-border, #E5E7EB);
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: var(--sdk-text-secondary, #6B7280);
    cursor: pointer;
    transition: all 0.2s;
  }

  .edit-btn:hover {
    border-color: var(--sdk-primary, #0D9488);
    color: var(--sdk-primary, #0D9488);
  }

  .section-content {
    padding: 16px;
  }

  .section-empty {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    color: var(--sdk-text-secondary, #9CA3AF);
    font-size: 14px;
  }

  /* Data Rows */
  .data-row {
    display: flex;
    align-items: flex-start;
    padding: 8px 0;
    border-bottom: 1px solid var(--sdk-border, #F3F4F6);
  }

  .data-row:last-child {
    border-bottom: none;
  }

  .data-icon {
    color: var(--sdk-text-secondary, #9CA3AF);
    margin-right: 8px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .data-label {
    font-size: 13px;
    color: var(--sdk-text-secondary, #6B7280);
    min-width: 140px;
    flex-shrink: 0;
  }

  .data-value {
    font-size: 13px;
    font-weight: 500;
    color: var(--sdk-text, #1F2937);
    flex: 1;
    word-break: break-word;
  }

  /* UBO Summary */
  .ubo-summary {
    padding: 12px;
    background: var(--sdk-bg-secondary, #F9FAFB);
    border-radius: 10px;
    margin-bottom: 12px;
  }

  .ubo-summary:last-of-type {
    margin-bottom: 0;
  }

  .ubo-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  .ubo-avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%);
    color: #059669;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .ubo-info {
    display: flex;
    flex-direction: column;
  }

  .ubo-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--sdk-text, #1F2937);
  }

  .ubo-ownership {
    font-size: 12px;
    color: var(--sdk-text-secondary, #6B7280);
  }

  .ubo-details {
    padding-left: 42px;
  }

  .ubo-details .data-row {
    padding: 4px 0;
  }

  .ubo-badge {
    display: inline-block;
    padding: 2px 8px;
    background: #E0E7FF;
    color: #4338CA;
    font-size: 11px;
    font-weight: 600;
    border-radius: 4px;
    margin-right: 6px;
    margin-top: 8px;
  }

  .ubo-badge.submitter {
    background: #D1FAE5;
    color: #059669;
  }

  .total-ownership {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 12px;
    background: #EFF6FF;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #1D4ED8;
    margin-top: 12px;
  }

  /* Documents List */
  .documents-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .document-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .document-check {
    color: #059669;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .document-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .document-type {
    font-size: 13px;
    font-weight: 500;
    color: var(--sdk-text, #1F2937);
  }

  .document-file {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--sdk-text-secondary, #9CA3AF);
  }

  /* Consent Section */
  .consent-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
  }

  .consent-checkbox {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px;
    background: var(--sdk-bg-secondary, #F9FAFB);
    border: 2px solid transparent;
    border-radius: 10px;
    cursor: pointer;
    transition: border-color 0.2s;
  }

  .consent-checkbox.error {
    border-color: #FECACA;
    background: #FEF2F2;
  }

  .consent-checkbox:has(input:checked) {
    border-color: var(--sdk-primary, #0D9488);
    background: rgba(var(--sdk-primary-rgb, 13, 148, 136), 0.05);
  }

  .consent-checkbox.certification {
    background: linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%);
  }

  .consent-checkbox input {
    display: none;
  }

  .checkbox-custom {
    width: 20px;
    height: 20px;
    border: 2px solid var(--sdk-border, #D1D5DB);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s;
  }

  .consent-checkbox input:checked + .checkbox-custom {
    background: var(--sdk-primary, #0D9488);
    border-color: var(--sdk-primary, #0D9488);
    color: white;
  }

  .consent-text {
    font-size: 13px;
    color: var(--sdk-text-secondary, #6B7280);
    line-height: 1.5;
  }

  .consent-text strong {
    color: var(--sdk-text, #1F2937);
  }

  .consent-text a {
    color: var(--sdk-primary, #0D9488);
    text-decoration: underline;
  }

  .consent-error {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #DC2626;
    margin-left: 32px;
    margin-top: -8px;
  }

  /* Submit Error */
  .submit-error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #FEF2F2;
    border: 1px solid #FECACA;
    border-radius: 10px;
    color: #DC2626;
    font-size: 14px;
    margin-bottom: 16px;
  }

  /* Actions */
  .step-actions {
    margin-bottom: 16px;
  }

  .primary-button {
    width: 100%;
    padding: 14px 24px;
    background: var(--sdk-primary, #0D9488);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;
  }

  .primary-button:hover:not(:disabled) {
    background: var(--sdk-primary-dark, #0F766E);
    transform: translateY(-1px);
  }

  .primary-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .security-notice {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 12px;
    color: var(--sdk-text-secondary, #9CA3AF);
    margin: 0;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @media (max-width: 480px) {
    .data-row {
      flex-direction: column;
      gap: 4px;
    }

    .data-label {
      min-width: auto;
    }

    .ubo-details {
      padding-left: 0;
    }
  }
`;
